import type { PostConfigObject } from "app/services/fetchService";

import { BaseThreadService } from "../../../../services/thread/baseThreadService";
import type { CreateThreadResponse } from "../../types/dto";

import type { ILeadGeneratorThreadService, ThreadOptions } from "./types";

const STORAGE_KEY = "si:ai-agent:leadGeneratorThread";

type LeadGeneratorSettings = {
    readonly isLeadGenAgentEnabled: boolean;
};

interface ILeadGeneratorApiService {
    createAIAgentThread: (options?: Readonly<PostConfigObject>) => Promise<CreateThreadResponse | null>;
}

interface ILeadGeneratorThreadServiceDependencies<Settings extends LeadGeneratorSettings> {
    readonly apiService: ILeadGeneratorApiService;

    readonly settings: Settings;
}

/**
 * Service for managing lead generator threads with persistence
 */
class LeadGeneratorThreadService
    extends BaseThreadService<CreateThreadResponse>
    implements ILeadGeneratorThreadService
{
    private static instance: LeadGeneratorThreadService;

    private settings: LeadGeneratorSettings;

    private apiService: ILeadGeneratorApiService;

    private baseStorageKey = STORAGE_KEY;

    private constructor(dependencies: ILeadGeneratorThreadServiceDependencies<LeadGeneratorSettings>) {
        super();

        this.settings = dependencies.settings;
        this.apiService = dependencies.apiService;
    }

    static initialize(
        dependencies: ILeadGeneratorThreadServiceDependencies<LeadGeneratorSettings>,
    ): LeadGeneratorThreadService {
        if (!LeadGeneratorThreadService.instance) {
            LeadGeneratorThreadService.instance = new LeadGeneratorThreadService(dependencies);
        }

        return LeadGeneratorThreadService.instance;
    }

    private createStorageKey(options: ThreadOptions = {}): string {
        const { savedSearchId } = options;

        if (savedSearchId) {
            return `${this.baseStorageKey}:${savedSearchId}`;
        }

        return this.baseStorageKey;
    }

    protected async createThreadInternal(_payload: unknown, options: PostConfigObject = {}): Promise<string> {
        const response = await this.apiService.createAIAgentThread(options);

        if (!response?.threadId) {
            throw new Error("Failed to extract threadId from thread creation response");
        }

        return response.threadId;
    }

    isFeatureEnabled(): boolean {
        return this.settings?.isLeadGenAgentEnabled ?? false;
    }

    getPersistedThreadWithOptions(options?: ThreadOptions): CreateThreadResponse | null {
        const storageKey = this.createStorageKey(options);

        return this.getPersistedThread(storageKey);
    }

    persistThreadWithOptions(threadData: CreateThreadResponse, options?: ThreadOptions): void {
        const storageKey = this.createStorageKey(options);

        this.persistThread(storageKey, threadData);
    }

    clearPersistedThreadWithOptions(options?: ThreadOptions): void {
        const storageKey = this.createStorageKey(options);

        this.clearPersistedThread(storageKey);
    }

    getExistingThreadId(options: ThreadOptions = {}): string | null {
        // Check if we have persisted data for this specific context (saved search or regular)
        const persistedData = this.getPersistedThreadWithOptions(options);

        if (persistedData?.threadId) {
            return persistedData.threadId;
        }

        // No existing thread found
        return null;
    }

    canPersistThread(threadId?: string): boolean {
        return this.isFeatureEnabled() && !!threadId;
    }
}

export default LeadGeneratorThreadService;
